import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { AlarmClock } from '@treasury/alarm-clock';
import { AchExceptionDetailSearchModelDto } from '@treasury/api/channel';
import { NavigationService } from '@treasury/core/navigation';
import { AchException } from '@treasury/domain/arp';
import { PayOrReturnChoice } from '@treasury/domain/channel/mappings/ach';
import { CompanyAccount } from '@treasury/domain/channel/services/positive-pay/entities/company-account';
import { PositivePayService } from '@treasury/domain/channel/services/positive-pay/positive-pay-service';
import '@treasury/omega/components/progress/omega-progress.js';
import { TmContainer } from '@treasury/presentation';
import '@treasury/presentation/components/forms/tm-form-row';
import '@treasury/presentation/components/layout/tm-section';
import { DownloadPageType } from '@treasury/presentation/components/tm-download.types';
import '@treasury/presentation/components/tm-labeled-list';
import '@treasury/presentation/components/tm-tabs';
import { MediaService } from '@treasury/utils/services/media-service';
import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import '../../../components/confirmation-container';
import { HeaderBarService } from '../../../services/jhd-header-bar.service';
import { defaultAchExceptionsActivitySearchRequest } from '../data/default-ach-exceptions-activity-search-request';
import { defaultAchExceptionsSearchRequest } from '../data/default-ach-exceptions-search-request';
import {
    ACH_EXCEPTION_CUTOFF,
    ACH_EXCEPTION_CUTOFF_MINUS_30,
    ACH_EXCEPTION_START,
} from '../partials/ach-exception-cutoff';
import '../partials/ach-exception-detail';
import '../partials/ach-exceptions-table';
import { AchExceptionActivityVm } from '../view-models/ach-exception-activity';
import './ach-exception-workflow-container';

enum AchExceptionsConfig {
    List = 'list',
    Review = 'review',
    Confirm = 'confirm',
}

export const tagName = 'ach-exceptions-container';
@customElement(tagName)
export class AchExceptionsContainer extends TmContainer {
    @InjectProperty()
    declare private mediaService: MediaService;

    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private positivePayService: PositivePayService;

    @state()
    private achExceptions!: AchException[];

    @state()
    private achExceptionActivity: AchExceptionActivityVm[] = [];

    @state()
    private exceptionDetailVisible = false;

    @state()
    private step = AchExceptionsConfig.List;

    @state()
    private tabs = [{ label: 'To Decision', id: 'ach-exceptions' }];

    @state()
    private activeTab: 'ach-exceptions' | 'ach-exceptions-activity' = 'ach-exceptions';

    @state()
    private selectedException!: AchException;

    @state()
    private accounts: CompanyAccount[] = [];

    @state()
    private secCodes: { secCode?: string; description?: string }[] = [];

    @state()
    private cutoffMessage?: string;

    @state()
    private afterCutoff = false;

    @state()
    private defaultRequest = defaultAchExceptionsSearchRequest;

    @state()
    private defaultActivityRequest = defaultAchExceptionsActivitySearchRequest;

    @state()
    private downloadFilters = {};

    private get exceptionType() {
        return window.location.pathname.split('/').pop();
    }

    private get fiClock() {
        return AlarmClock.getInstance();
    }

    public async firstUpdated() {
        this.initHeader();
        Promise.all([this.setCutoffMessage(), this.fetchAccounts(), this.fetchSecCodes()]);
    }

    public render() {
        return html` ${this.renderLoader()}${this.renderWorkflow()} ${this.renderExceptionDetail()}`;
    }

    protected displayDetailSheet(exception: AchException) {
        this.selectedException = exception;
        this.exceptionDetailVisible = true;
    }

    private async fetchAccounts() {
        await this.tryFetch(
            async () => this.positivePayService.getArpAchExceptionAccounts(),
            result => {
                if (result) {
                    this.accounts = result;
                    this.defaultRequest.accounts = this.accounts as [];
                }
            }
        );
    }

    private async fetchSecCodes() {
        await this.tryFetch(
            async () => this.positivePayService.getArpSecCodes(),
            result => {
                if (result) {
                    this.secCodes = result;
                    this.defaultRequest.secCodes = this.secCodes as [];
                }
            }
        );
    }

    private async fetchAchExceptions(
        filters: AchExceptionDetailSearchModelDto = this.defaultRequest
    ) {
        const mappedFilters = {
            ...filters,
            // PATCH: This code needs to live in the service but should be fixed at a later date
            accounts: filters.accounts?.map((a: unknown) => (a as CompanyAccount).toDto()),
        };
        await this.tryFetch(
            async () => this.positivePayService.searchAchExceptions(mappedFilters),
            result => {
                if (result) {
                    this.achExceptions = result;
                }
            }
        );
        this.downloadFilters = mappedFilters;
        this.initHeader();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private async fetchAchExceptionsActivity(filters: any = this.defaultActivityRequest) {
        await this.tryFetch(
            async () => this.positivePayService.searchAchExceptionsActivity(filters),
            result => {
                if (result) {
                    this.achExceptionActivity = result.map(ex => new AchExceptionActivityVm(ex));
                }
            }
        );
    }

    private async goToCheckExceptions() {
        await this.navService.navigate('/positive-pay/check-exceptions');
    }

    private async setCutoffMessage() {
        await this.fiClock.checkReadiness();
        if (
            this.fiClock.isAfter(ACH_EXCEPTION_CUTOFF_MINUS_30) &&
            this.fiClock.isBefore(ACH_EXCEPTION_CUTOFF)
        ) {
            this.cutoffMessage = `ACH exception items will be disabled for decisioning at ${this.fiClock.getAlarm(
                ACH_EXCEPTION_CUTOFF
            )} Cut-Off time (${this.fiClock.timeZone}).`;
            this.notificationService.renderWarning(this.cutoffMessage);
        } else if (this.fiClock.isAfter(ACH_EXCEPTION_CUTOFF)) {
            this.afterCutoff = true;
            this.cutoffMessage = `ACH exception items are disabled because the current time is past the ${this.fiClock.getAlarm(
                ACH_EXCEPTION_CUTOFF
            )} Cut-Off time (${this.fiClock.timeZone})`;
            this.notificationService.renderWarning(this.cutoffMessage);
        } else if (this.fiClock.isBefore(ACH_EXCEPTION_START)) {
            this.afterCutoff = true;
            this.cutoffMessage = `ACH exception items are not available at this time. Please try again after ${this.fiClock.getAlarm(
                ACH_EXCEPTION_START
            )} (${this.fiClock.timeZone}).`;
            this.notificationService.renderWarning(this.cutoffMessage);
        } else {
            this.cutoffMessage = undefined;
        }
    }

    private initHeader() {
        this.headerService.configure({
            title: 'Positive Pay - ACH',
            menuItems: [
                {
                    title: 'Download',
                    icon: html`<tm-download
                        .page=${DownloadPageType.AchExceptions}
                        .filterModel=${this.downloadFilters}
                        .disabled=${!this.achExceptions?.length}
                    ></tm-download>`,
                    action: () => undefined,
                },
            ],
        });
    }

    private goToReviewStep() {
        this.step = AchExceptionsConfig.Review;
        this.headerService.configure({
            backAction: () => {
                this.step = AchExceptionsConfig.List;
                this.initHeader();
            },
            title: 'Review Decisions',
        });
    }

    private goToConfirmStep() {
        this.step = AchExceptionsConfig.Confirm;
        this.headerService.configure({});
    }

    private goToLandingTable() {
        this.step = AchExceptionsConfig.List;
        this.initHeader();
    }

    private switchExceptionTab(tabId: 'ach-exceptions' | 'ach-exceptions-activity') {
        this.activeTab = tabId;
    }

    private async confirm(achExceptions: AchException[]) {
        await this.tryFetch(
            async () => this.positivePayService.updateExceptionStatuses(achExceptions),
            result => {
                if (!result) {
                    this.loading = false;
                    this.notificationService.renderError('Failed to update ACH exceptions.');
                    return;
                }
                if (result) {
                    this.goToConfirmStep();
                }
            }
        );
    }

    private decisionAll(decision: PayOrReturnChoice) {
        this.achExceptions?.forEach((e: AchException) => {
            this.updateExceptionDecision(decision, e);
        });
        this.achExceptions = this.achExceptions.map(e => e.clone());
    }

    private updateExceptionDecision(decision: PayOrReturnChoice, item: AchException) {
        switch (decision) {
            case PayOrReturnChoice.Pay:
                item.pay();
                return;
            case PayOrReturnChoice.Return:
                item.return();
                return;
            case PayOrReturnChoice.Unselected:
                item.clearDecision();
                return;
            default:
                item.clearDecision();
                throw new Error(`Invalid decision type: "${decision}"`);
        }
    }

    private renderWorkflow() {
        const tabs =
            this.step === AchExceptionsConfig.List
                ? html`
                      <tm-tabs
                          class="bg-primary"
                          .tabs=${this.tabs}
                          .activeTab=${this.exceptionType}
                          @switchTab=${(event: CustomEvent) =>
                              this.switchExceptionTab(event.detail.activeTab)}
                      >
                      </tm-tabs>
                  `
                : nothing;
        return html`<tm-section
            class=${classMap({
                'flex flex-col': true,
                'overflow-hidden h-full': !this.isDesktop,
                'mx-2 mb-12 mt-2 pt-2 rounded-lg h-[70vh] pb-[25px]': this.isDesktop,
            })}
            >${tabs}<ach-exception-workflow-container
                .loading=${this.loading}
                .step=${this.step}
                .activeTab=${this.activeTab}
                .afterCutoff=${this.afterCutoff}
                .achExceptions=${this.achExceptions}
                .achExceptionActivity=${this.achExceptionActivity}
                .accounts=${this.accounts}
                .secCodes=${this.secCodes}
                @selected=${(event: CustomEvent<AchException>) => {
                    this.displayDetailSheet(event.detail);
                }}
                @decisionAll=${(event: CustomEvent<PayOrReturnChoice>) => {
                    this.decisionAll(event.detail);
                }}
                @filter=${(e: CustomEvent) => {
                    if (!this.achExceptions) {
                        this.fetchAchExceptions(e.detail);
                        return;
                    }
                    if (
                        this.achExceptions.some(
                            a => a.decisionChoice !== PayOrReturnChoice.Unselected
                        )
                    ) {
                        return;
                    }
                    this.fetchAchExceptions(e.detail);
                }}
                @filter-activity=${(e: CustomEvent) => {
                    this.fetchAchExceptionsActivity(e.detail);
                }}
                @review=${() => {
                    this.goToReviewStep();
                }}
                @confirm=${(e: CustomEvent<AchException[]>) => {
                    this.confirm(e.detail);
                }}
                @list=${({ detail }: CustomEvent) => {
                    if (detail.reload) {
                        this.fetchAchExceptions();
                    }
                    this.goToLandingTable();
                }}
                @check-exceptions=${() => {
                    this.goToCheckExceptions();
                }}
            ></ach-exception-workflow-container
        ></tm-section>`;
    }

    private renderExceptionDetail() {
        return html`<ach-exception-detail
            .open=${this.exceptionDetailVisible}
            .loading=${this.loading}
            .hideActions=${this.afterCutoff}
            @close=${() => {
                this.exceptionDetailVisible = false;
            }}
            @pay=${async () => {
                this.updateExceptionDecision(PayOrReturnChoice.Pay, this.selectedException);
                await this.confirm([this.selectedException]);
                this.exceptionDetailVisible = false;
            }}
            @return=${async () => {
                this.updateExceptionDecision(PayOrReturnChoice.Return, this.selectedException);
                await this.confirm([this.selectedException]);
                this.exceptionDetailVisible = false;
            }}
            .selectedException=${this.selectedException}
        ></ach-exception-detail>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                }
                ach-exceptions-table {
                    width: 100vw;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AchExceptionsContainer;
    }
}
